/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '@/components/providers/ThemeProvider';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { supabaseClient } from '@/lib/supabase';

import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  onPasswordReset: (newPassword: string) => Promise<void>;
  email: string | undefined;
  title: string;
  subtitle?: string;
}

export const ResetPasswordForm = ({
  onPasswordReset,
  email,
  title,
  subtitle,
}: Props): JSX.Element => {
  const { resolvedTheme } = useTheme();
  const logoSrc =
    resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg';

  const { formatMessage } = useIntl();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      ...(email !== undefined ? { email } : undefined),
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['auth', 'reset-password'],
    mutationFn: async (values: FormValues) => {
      const { error } = await supabaseClient.auth.updateUser({
        password: values.password,
      });

      if (error !== null) {
        throw error;
      }

      return values.password;
    },
    onSuccess: async password => {
      toast({
        title: 'auth.resetPassword.form.submit.success',
        variant: 'default',
      });
      await onPasswordReset(password);
    },
    onError: () => {
      toast({
        title: 'auth.resetPassword.form.submit.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <section className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-100 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400 to-indigo-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-br from-purple-300 to-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <Link
          to="/"
          className="flex items-center mb-8 text-2xl font-semibold text-gray-900 dark:text-white animate-fade-in"
        >
          <img
            className="mr-2 drop-shadow-lg"
            src={logoSrc}
            alt="logo"
            width={120}
            height={100}
          />
        </Link>

        <div className="w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 dark:bg-gray-800/80 dark:border-gray-700/50 md:mt-0 sm:max-w-md xl:p-0 transform transition-all duration-500 hover:shadow-3xl hover:scale-105 animate-slide-up">
          <div className="p-8 space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 dark:from-purple-400 dark:to-pink-400 bg-clip-text text-transparent md:text-3xl">
                {title}
              </h1>
              {subtitle !== undefined && (
                <h2 className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-300 md:text-base">
                  {subtitle}
                </h2>
              )}
              <div className="mt-2 h-1 w-16 bg-gradient-to-r from-purple-500 to-pink-500 dark:from-purple-400 dark:to-pink-400 rounded-full mx-auto"></div>
            </div>
            <Form {...form}>
              <form
                className="space-y-4 md:space-y-6"
                onSubmit={form.handleSubmit(async values => {
                  await mutateAsync(values);
                })}
              >
                {email !== undefined && (
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>
                          <FormattedMessage id="auth.resetPassword.form.email" />
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            type="text"
                            disabled
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        <FormattedMessage id="auth.resetPassword.form.password.label" />
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            {...field}
                            className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={formatMessage({
                              id: 'auth.resetPassword.form.password.placeholder',
                            })}
                          />
                          <Button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                          >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 focus:ring-4 focus:outline-none focus:ring-purple-300 font-semibold rounded-lg text-sm px-5 py-2.5 text-center dark:focus:ring-purple-800 shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  disabled={isPending}
                >
                  {isPending ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <FormattedMessage id="auth.resetPassword.form.submit.pending" />
                    </div>
                  ) : (
                    <FormattedMessage id="auth.resetPassword.form.submit.label" />
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};
